/**
 * LogEntryModal.js
 * 모바일 시간 입력/수정용 하단 모달 (Bottom Sheet)
 * Mobile First 전략 - 390px 기준, 44px 터치 영역
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SwipeableDrawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Autocomplete,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Checkbox,
  Paper,
  ListItem,
  ListItemText,
} from '@mui/material';
import { X, ChevronUp, ChevronDown, CheckSquare, Search, Clock } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { format } from 'date-fns';
import axios from 'axios';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const LogEntryModal = ({
  open,
  onClose,
  logData = null, // 신규: { start, end }, 수정: { id, ... }, ToDo: { fromTodo: true, todoId, ... }
  contracts = [],
  companies = [],
  onSave,
  onDelete,
}) => {
  const { colors, isDark } = useCustomTheme();
  const [formData, setFormData] = useState({
    log_date: format(new Date(), 'yyyy-MM-dd'),
    company: null,
    contract: null,
    job_category: '',
    input_description: '',
    input_minute: 60,
    start_time: '09:00',
    end_time: '10:00',
  });
  const [contractOptions, setContractOptions] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  const [isSearchingContracts, setIsSearchingContracts] = useState(false);
  const [todos, setTodos] = useState([]);
  const [loadingTodos, setLoadingTodos] = useState(false);
  const [selectedTodoId, setSelectedTodoId] = useState(null);

  // 초기 데이터 설정
  useEffect(() => {
    if (logData) {
      if (logData.fromTodo) {
        // To-Do에서 변환된 경우
        const company = logData.company_code ? companies.find(c => c.company_code === logData.company_code) : null;
        setFormData({
          log_date: logData.log_date || format(new Date(), 'yyyy-MM-dd'),
          company: company || null,
          contract: logData.contract || null,
          job_category: logData.job_category || '',
          input_description: logData.input_description || '',
          input_minute: logData.input_minute || 60,
          start_time: logData.start_time || '09:00',
          end_time: logData.end_time || '10:00',
        });
      } else if (logData.id) {
        // 기존 로그 수정
        const company = logData.company_code ? companies.find(c => c.company_code === logData.company_code) : null;
        setFormData({
          log_date: logData.log_date || format(new Date(), 'yyyy-MM-dd'),
          company: company || null,
          contract: logData.contract || null,
          job_category: logData.job_category || '',
          input_description: logData.input_description || '',
          input_minute: logData.input_minute || 60,
          start_time: logData.start_time || '09:00',
          end_time: logData.end_time || '10:00',
        });
      } else {
        // 신규 입력 (드래그 선택)
        const startDate = logData.start ? format(new Date(logData.start), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
        const startTime = logData.start ? format(new Date(logData.start), 'HH:mm') : '09:00';
        const endTime = logData.end ? format(new Date(logData.end), 'HH:mm') : '10:00';
        
        // 시간 차이 계산 (분 단위)
        const start = logData.start ? new Date(logData.start) : new Date();
        const end = logData.end ? new Date(logData.end) : new Date();
        const diffMinutes = Math.round((end - start) / (1000 * 60));
        
        setFormData({
          log_date: startDate,
          company: null,
          contract: null,
          job_category: '',
          input_description: '',
          input_minute: diffMinutes > 0 ? diffMinutes : 60,
          start_time: startTime,
          end_time: endTime,
        });
      }
    } else {
      // 초기화
      setFormData({
        log_date: format(new Date(), 'yyyy-MM-dd'),
        company: null,
        contract: null,
        job_category: '',
        input_description: '',
        input_minute: 60,
        start_time: '09:00',
        end_time: '10:00',
      });
    }
  }, [logData, open, companies]);

  // 최근 사용 계약 조회
  const fetchRecentContracts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/recent`, {
        params: { limit: 10 }
      });
      const recentData = (response.data || []).map(c => {
        const companyName = c.company_name_kr || '';
        const contractTitle = c.contract_title || '';
        return {
          ...c,
          isRecent: true,
          display_label: contractTitle && companyName
            ? `${companyName} - ${contractTitle}`.trim()
            : companyName || contractTitle || ''
        };
      });
      setRecentContracts(recentData);
    } catch (error) {
      console.error('최근 계약 조회 실패:', error);
      setRecentContracts([]);
    }
  }, []);

  // 계약 통합 검색 (디바운싱)
  const searchContracts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContractOptions([]);
      return;
    }

    setIsSearchingContracts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/search`, {
        params: { q: query, limit: 20 }
      });
      const searchData = (response.data || []).map(c => {
        const companyName = c.company_name_kr || '';
        const contractTitle = c.contract_title || '';
        return {
          ...c,
          display_label: contractTitle && companyName
            ? `${companyName} - ${contractTitle}`.trim()
            : companyName || contractTitle || ''
        };
      });
      setContractOptions(searchData);
    } catch (error) {
      console.error('계약 검색 실패:', error);
      setContractOptions([]);
    } finally {
      setIsSearchingContracts(false);
    }
  }, []);

  // To-Do 리스트 조회
  const fetchTodos = useCallback(async () => {
    setLoadingTodos(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/todos`, {
        params: { status: 'active' }
      });
      setTodos(response.data || []);
    } catch (error) {
      console.error('To-Do 리스트 조회 실패:', error);
      setTodos([]);
    } finally {
      setLoadingTodos(false);
    }
  }, []);


  // 모달이 열릴 때 최근 계약 조회
  useEffect(() => {
    if (open) {
      fetchRecentContracts();
    }
  }, [open, fetchRecentContracts]);

  // 계약 검색 디바운싱
  useEffect(() => {
    if (!contractSearchQuery || contractSearchQuery.length < 2) {
      setContractOptions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchContracts(contractSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [contractSearchQuery, searchContracts]);

  // 통합 검색 옵션 (최근 사용 + 검색 결과)
  const allContractOptions = React.useMemo(() => {
    const recent = recentContracts.map(c => ({ ...c, isRecent: true }));
    const search = contractOptions.filter(
      opt => !recentContracts.some(rc => rc.contract_id === opt.contract_id && rc.company_code === opt.company_code)
    );
    
    if (recent.length > 0 && search.length > 0) {
      return [
        ...recent,
        { divider: true },
        ...search,
      ];
    }
    return recent.length > 0 ? recent : search;
  }, [recentContracts, contractOptions]);

  // 모달이 열릴 때 To-Do 리스트 조회
  useEffect(() => {
    if (open) {
      fetchTodos();
    } else {
      setTodos([]);
      setSelectedTodoId(null);
    }
  }, [open, fetchTodos]);


  const handleContractChange = (event, newValue) => {
    setFormData(prev => ({ ...prev, contract: newValue }));
    // 계약 선택 시 업체도 자동 설정
    if (newValue && newValue.company_code) {
      const company = companies.find(c => c.company_code === newValue.company_code);
      if (company) {
        setFormData(prev => ({ ...prev, company: company, contract: newValue }));
      }
    }
  };


  const handleInputMinuteChange = (delta) => {
    setFormData(prev => ({
      ...prev,
      input_minute: Math.max(0, (prev.input_minute || 0) + delta)
    }));
  };

  // 스와이프 제스처를 위한 ref 및 상태
  const swipeContainerRef = useRef(null);
  const inputFieldRef = useRef(null);
  const swipeStartX = useRef(0);
  const swipeCurrentX = useRef(0);
  const prevMinutesRef = useRef(0);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // 스와이프 시작
  const handleSwipeStart = (e) => {
    // 입력 필드에 포커스가 있으면 스와이프 비활성화
    if (isInputFocused) {
      return;
    }
    e.preventDefault();
    const touch = e.touches ? e.touches[0] : { clientX: e.clientX };
    if (swipeContainerRef.current) {
      const rect = swipeContainerRef.current.getBoundingClientRect();
      swipeStartX.current = touch.clientX - rect.left; // 컨테이너 기준 상대 위치
      swipeCurrentX.current = swipeStartX.current;
    } else {
      swipeStartX.current = touch.clientX;
      swipeCurrentX.current = touch.clientX;
    }
    setIsSwiping(true);
    setSwipeProgress(0);
    prevMinutesRef.current = formData.input_minute || 0;
    // 햅틱 피드백
    if (navigator.vibrate) {
      navigator.vibrate(5);
    }
  };

  // 스와이프 중
  const handleSwipeMove = (e) => {
    if (!isSwiping) return;
    e.preventDefault();
    
    const touch = e.touches ? e.touches[0] : { clientX: e.clientX };
    
    if (swipeContainerRef.current) {
      const rect = swipeContainerRef.current.getBoundingClientRect();
      swipeCurrentX.current = touch.clientX - rect.left; // 컨테이너 기준 상대 위치
      const containerWidth = rect.width;
      
      // 스와이프 위치를 0~1 비율로 계산
      const ratio = Math.max(0, Math.min(1, swipeCurrentX.current / containerWidth));
      setSwipeProgress(ratio);
      
      // 비율에 따라 시간 계산 (0분 ~ 120분)
      const minutes = Math.round(ratio * 120);
      setFormData(prev => ({
        ...prev,
        input_minute: minutes
      }));
      
      // 10분 단위마다 햅틱 피드백
      if (Math.floor(minutes / 10) !== Math.floor(prevMinutesRef.current / 10)) {
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      }
      prevMinutesRef.current = minutes;
    }
  };

  // 스와이프 종료
  const handleSwipeEnd = () => {
    if (!isSwiping) return;
    
    // 최종 시간은 이미 handleSwipeMove에서 설정되었으므로 여기서는 햅틱 피드백만
    if (navigator.vibrate) {
      navigator.vibrate(20);
    }
    
    // 초기화
    setIsSwiping(false);
    setSwipeProgress(0);
    swipeStartX.current = 0;
    swipeCurrentX.current = 0;
  };

  // To-Do 체크박스 선택 시 자동 채우기
  const handleTodoSelect = (todo) => {
    if (selectedTodoId === todo.todo_id) {
      // 이미 선택된 경우 해제
      setSelectedTodoId(null);
      return;
    }

    setSelectedTodoId(todo.todo_id);

    // 업체 설정
    let company = null;
    if (todo.company_code) {
      company = companies.find(c => c.company_code === todo.company_code) || todo.company_code;
    }

    // 계약 설정
    let contract = null;
    if (todo.contract_id) {
      contract = contracts.find(c => c.contract_id === todo.contract_id);
    }

    // 폼 데이터 자동 채우기
    setFormData(prev => ({
      ...prev,
      company: company,
      contract: contract,
      job_category: todo.job_category || '',
      input_description: todo.note_1 || '',
    }));
  };

  const handleSubmit = () => {
    if (!formData.company || !formData.contract || !formData.input_minute) {
      alert('업체, 계약번호, 투입 시간은 필수 입력 항목입니다.');
      return;
    }
    
    if (!formData.input_description || !formData.input_description.trim()) {
      alert('업무 내용을 입력해주세요.');
      return;
    }

    const companyCode = typeof formData.company === 'string' ? formData.company : formData.company.company_code;

    const submitData = {
      log_date: formData.log_date,
      company_code: companyCode,
      contract_id: formData.contract.contract_id,
      job_category: formData.job_category?.trim() || null,
      input_description: formData.input_description.trim(),
      input_minute: formData.input_minute,
    };

    if (onSave) {
      onSave(submitData, logData?.id);
    }
  };

  const handleDelete = () => {
    if (logData?.id && window.confirm('정말 삭제하시겠습니까?')) {
      if (onDelete) {
        onDelete(logData.id);
      }
    }
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: {
          height: '90%',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          backgroundColor: colors.secondaryBg,
          maxHeight: '844px', // iPhone 12/13/14 Pro 기준
          marginRight: '32px', // 오른쪽 사이드 바와의 간격
          right: '0px', // 오른쪽 정렬
        },
      }}
    >
      <Box sx={{ p: 2, pr: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600, fontSize: '18px' }}>
            {logData?.id ? '투입시간 수정' : '투입시간 입력'}
          </Typography>
          <IconButton onClick={onClose} size="large" sx={{ minWidth: '44px', minHeight: '44px' }}>
            <X size={24} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* 폼 내용 */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
          touchAction: 'pan-y', // 수직 스크롤만 허용
          pt: 3, // 상단 여백 추가 (더 여유있게)
          pb: 2, 
          pr: 2 
        }}>

          {/* 업무일자 입력 */}
          <Box sx={{ mb: 2 }}>
            <TextField
              type="date"
              label="업무일자"
              value={formData.log_date}
              onChange={(e) => setFormData(prev => ({ ...prev, log_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              inputProps={{
                style: { fontSize: '16px', minHeight: '44px', paddingRight: '12px' }
              }}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '14px',
                },
                '& .MuiInputBase-root': {
                  paddingRight: '8px', // 캘린더 아이콘과 텍스트 사이 여백 확보
                },
                '& .MuiInputBase-input[type="date"]': {
                  paddingRight: '40px', // 캘린더 아이콘 공간 확보
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                  opacity: 1,
                  cursor: 'pointer',
                  marginLeft: '8px', // 텍스트와 아이콘 사이 여백
                },
                '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                  opacity: 0.7,
                },
              }}
            />
          </Box>

          {/* 통합 계약 검색 (업체+계약 통합) */}
          <Box sx={{ mb: 1.5 }}>
            <Autocomplete
              options={allContractOptions}
              getOptionLabel={(option) => {
                if (option.divider) return '';
                if (option.display_label) return option.display_label;
                const companyName = option.company_name_kr || '';
                const contractTitle = option.contract_title || '';
                return contractTitle && companyName
                  ? `${companyName} - ${contractTitle}`.trim()
                  : companyName || contractTitle || '';
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value || option.divider) return false;
                if (option.contract_id && value.contract_id) {
                  return option.contract_id === value.contract_id && option.company_code === value.company_code;
                }
                return option.company_code === value.company_code;
              }}
              value={formData.contract}
              onChange={(_, newValue) => {
                if (newValue && !newValue.divider) {
                  handleContractChange(_, newValue);
                }
              }}
              onInputChange={(_, newInputValue) => {
                setContractSearchQuery(newInputValue);
                if (newInputValue.length >= 2) {
                  searchContracts(newInputValue);
                }
              }}
              loading={isSearchingContracts}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="업체/계약 검색"
                  placeholder="업체명 또는 계약명을 입력하세요"
                  margin="normal"
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: '16px', minHeight: '44px' }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search size={18} style={{ marginRight: 8, color: colors.secondaryText }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '14px',
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                if (option.divider) {
                  return <Divider key="divider" />;
                }
                return (
                  <ListItem {...props} key={`${option.contract_id}-${option.company_code}`}>
                    <ListItemText
                      primary={option.display_label || (() => {
                        const companyName = option.company_name_kr || '';
                        const contractTitle = option.contract_title || '';
                        return contractTitle && companyName
                          ? `${companyName} - ${contractTitle}`.trim()
                          : companyName || contractTitle || '';
                      })()}
                      secondary={option.contract_id}
                    />
                  </ListItem>
                );
              }}
              noOptionsText="2자 이상 입력하세요"
            />
          </Box>

          {/* 업무 구분 */}
          <Box sx={{ mb: 1.5 }}>
            <Autocomplete
              freeSolo
              options={['정산', '정정', '보고서', '데이터 분석']}
              value={formData.job_category}
              onChange={(event, newValue) => {
                // freeSolo이므로 newValue는 문자열이거나 null일 수 있음
                setFormData(prev => ({ ...prev, job_category: newValue || '' }));
              }}
              onInputChange={(event, newInputValue, reason) => {
                // reason이 'input'일 때만 상태 업데이트 (드롭다운 선택 시 중복 방지)
                if (reason === 'input' || reason === 'clear') {
                  setFormData(prev => ({ ...prev, job_category: newInputValue }));
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="업무 구분"
                  placeholder="예: 정산, 정정, 보고서 (자유 입력 가능)"
                  margin="normal"
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: '16px', minHeight: '44px' }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '14px',
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* 투입 시간 (분) - 터치 최적화된 증감 버튼 */}
          <Box sx={{ mb: 1.5 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: colors.secondaryText, 
                mb: 1,
                fontSize: '12px',
                display: 'block',
              }}
            >
              투입시간(분)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => handleInputMinuteChange(-15)}
                sx={{ 
                  minWidth: '48px', 
                  minHeight: '48px',
                  border: `1px solid ${colors.primaryBorder}`,
                  backgroundColor: isDark ? '#2A2B30' : '#F8F9FA',
                  '&:hover': {
                    backgroundColor: isDark ? '#333' : '#E0E0E0',
                  },
                }}
              >
                <ChevronDown size={20} />
              </IconButton>
              <Box
                ref={swipeContainerRef}
                onTouchStart={handleSwipeStart}
                onTouchMove={handleSwipeMove}
                onTouchEnd={handleSwipeEnd}
                onMouseDown={handleSwipeStart}
                onMouseMove={handleSwipeMove}
                onMouseUp={handleSwipeEnd}
                onMouseLeave={handleSwipeEnd}
                sx={{
                  position: 'relative',
                  flex: 1,
                  touchAction: isInputFocused ? 'manipulation' : 'pan-x',
                  userSelect: isInputFocused ? 'text' : 'none',
                }}
              >
                <TextField
                  inputRef={inputFieldRef}
                  type="number"
                  value={formData.input_minute}
                  onChange={(e) => {
                    const value = e.target.value;
                    // 빈 문자열 허용 (입력 중일 때)
                    if (value === '' || value === null || value === undefined) {
                      setFormData(prev => ({ ...prev, input_minute: '' }));
                      return;
                    }
                    // 정수만 허용
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setFormData(prev => ({ ...prev, input_minute: numValue }));
                    }
                  }}
                  onFocus={() => {
                    setIsInputFocused(true);
                    // 포커스 시 스와이프 상태 초기화
                    setIsSwiping(false);
                    setSwipeProgress(0);
                  }}
                  onBlur={(e) => {
                    setIsInputFocused(false);
                    // 빈 값이면 0으로 설정
                    const value = parseInt(e.target.value, 10);
                    if (isNaN(value) || value < 0) {
                      setFormData(prev => ({ ...prev, input_minute: 0 }));
                    }
                  }}
                  fullWidth
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: { 
                      fontSize: '18px', 
                      minHeight: '48px',
                      textAlign: 'center',
                      fontWeight: 600,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '18px',
                      backgroundColor: isSwiping && swipeProgress > 0 && !isInputFocused
                        ? `rgba(138, 124, 255, ${0.1 + swipeProgress * 0.3})`
                        : 'transparent',
                      transition: isSwiping && !isInputFocused ? 'none' : 'background-color 0.2s ease',
                    },
                  }}
                />
                {/* 스와이프 진행도 표시 (좌에서 우로, 0분~120분) */}
                {isSwiping && swipeProgress > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${swipeProgress * 100}%`,
                      backgroundColor: `rgba(138, 124, 255, ${0.2 + swipeProgress * 0.3})`,
                      borderRadius: '4px',
                      pointerEvents: 'none',
                      transition: 'none',
                      zIndex: 1,
                    }}
                  />
                )}
              </Box>
              <IconButton
                onClick={() => handleInputMinuteChange(15)}
                sx={{ 
                  minWidth: '48px', 
                  minHeight: '48px',
                  border: `1px solid ${colors.primaryBorder}`,
                  backgroundColor: isDark ? '#2A2B30' : '#F8F9FA',
                  '&:hover': {
                    backgroundColor: isDark ? '#333' : '#E0E0E0',
                  },
                }}
              >
                <ChevronUp size={20} />
              </IconButton>
            </Box>
            {/* 빠른 시간 선택 버튼 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
              {[30, 60, 90, 120, 180, 240].map((minutes) => (
                <Chip
                  key={minutes}
                  label={`${minutes}분`}
                  onClick={() => setFormData(prev => ({ ...prev, input_minute: minutes }))}
                  size="small"
                  sx={{
                    flex: 1,
                    backgroundColor: formData.input_minute === minutes
                      ? (isDark ? 'rgba(138,124,255,0.2)' : 'rgba(138,124,255,0.1)')
                      : (isDark ? '#2A2B30' : '#F8F9FA'),
                    color: colors.primaryText,
                    minHeight: '36px',
                    fontSize: '11px',
                    fontWeight: formData.input_minute === minutes ? 600 : 400,
                    border: formData.input_minute === minutes
                      ? `2px solid ${colors.accent}`
                      : `1px solid ${colors.primaryBorder}`,
                    padding: '4px 8px',
                    '& .MuiChip-label': {
                      padding: '0 4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                    '&:hover': {
                      backgroundColor: isDark ? '#333' : '#E0E0E0',
                    },
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* 업무 내용 상세 */}
          <TextField
            label="업무내용 상세"
            value={formData.input_description}
            onChange={(e) => setFormData(prev => ({ ...prev, input_description: e.target.value }))}
            fullWidth
            multiline
            rows={1.5}
            margin="normal"
            placeholder="업무 내용을 입력하세요 (Ctrl+Enter로 저장)"
            required
            onKeyDown={(e) => {
              // Ctrl+Enter 또는 Cmd+Enter로 저장 (Shift+Enter는 줄바꿈)
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit();
              }
            }}
            sx={{ 
              mb: 1.5,
              '& .MuiInputLabel-root': {
                fontSize: '12px',
              },
            }}
            inputProps={{
              style: { fontSize: '12px' }
            }}
          />

          {/* To-Do 리스트 */}
          <Box sx={{ mb: 1.5 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 1.5, 
                color: colors.secondaryText, 
                fontWeight: 600, 
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <CheckSquare size={16} />
              To-Do 리스트
            </Typography>
            {loadingTodos ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : todos.length > 0 ? (
              <Box sx={{ 
                maxHeight: '200px', 
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
                touchAction: 'pan-y', // 수직 스크롤만 허용
              }}>
                {todos.map((todo) => (
                  <Paper
                    key={todo.todo_id}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      backgroundColor: selectedTodoId === todo.todo_id 
                        ? (isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)')
                        : (isDark ? '#2A2B30' : '#F8F9FA'),
                      border: selectedTodoId === todo.todo_id
                        ? `2px solid ${colors.accent}`
                        : `1px solid ${colors.primaryBorder}`,
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: isDark ? '#333' : '#F0F0F0',
                      },
                    }}
                    onClick={() => handleTodoSelect(todo)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Checkbox
                        checked={selectedTodoId === todo.todo_id}
                        onChange={() => handleTodoSelect(todo)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                        sx={{ 
                          mt: -0.5,
                          ml: -0.5,
                          padding: '4px',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600, 
                            color: colors.primaryText, 
                            mb: 0.5, 
                            fontSize: '12px',
                            wordBreak: 'break-word',
                          }}
                        >
                          {todo.note_1 || todo.work_description || '제목 없음'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {(() => {
                            // 업체명칭 찾기
                            const company = todo.company_code 
                              ? companies.find(c => c.company_code === todo.company_code)
                              : null;
                            const companyName = company?.company_name_kr || todo.company_code || '';
                            
                            // 계약명칭 찾기
                            const contract = todo.contract_id
                              ? contracts.find(c => c.contract_id === todo.contract_id)
                              : null;
                            const contractTitle = contract?.contract_title || todo.contract_id || '';
                            
                            return (
                              <>
                                {companyName && (
                                  <Chip 
                                    label={`업체: ${companyName}`}
                                    size="small" 
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '10px',
                                    }} 
                                  />
                                )}
                                {contractTitle && (
                                  <Chip 
                                    label={`계약: ${contractTitle}`}
                                    size="small" 
                                    sx={{ 
                                      height: 20, 
                                      fontSize: '10px',
                                    }} 
                                  />
                                )}
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="caption" sx={{ color: colors.secondaryText, fontSize: '12px' }}>
                  등록된 To-Do가 없습니다
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* 하단 버튼 */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2 }}>
          {logData?.id && (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              sx={{ 
                minHeight: '48px',
                flex: 1,
                borderColor: colors.error,
                color: colors.error,
                fontSize: '12px',
              }}
            >
              삭제
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ 
              minHeight: '48px',
              flex: 1,
              borderColor: colors.primaryBorder,
              color: colors.primaryText,
              fontSize: '12px',
            }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ 
              backgroundColor: colors.accent, 
              minHeight: '48px',
              flex: logData?.id ? 2 : 2,
              fontSize: '12px',
              '&:hover': {
                backgroundColor: colors.accent,
                opacity: 0.9,
              },
            }}
          >
            저장
          </Button>
        </Box>
      </Box>
    </SwipeableDrawer>
  );
};

export default LogEntryModal;

